import SelectedScheme from "components/Scheme/SelectedScheme";
import { assetSortEnum, assetTypeEnum } from "kubb";
import type React from "react";
import BetaAlert from "../../../../components/Beta/BetaAlert";
import CompanyNavLink from "../../../../components/navigation/CompanyNavLink";
import { OneCardPage } from "../../../../components/utils/OneCardPage";
import useGetParsedSearchString from "../../../../hooks/getParsedSearchString";
import { schemeObjectPageTitle } from "../../../../hooks/setPageTitle";
import AssetList from "../list/AssetList";
import GatewayConnectivityReport from "./GatewayConnectivityReport";
import { UnassignedGatewayConnectivityReportDataRow } from "./GatewayConnectivityReportDataRow";
import GatewayConnectivityReportHeaderRow from "./GatewayConnectivityReportHeaderRow";
import type { GatewayFilterParams } from "./GatewayFilterForm";

function parseGatewayFilterParams(
  params: Record<string, unknown>,
): GatewayFilterParams {
  // Parse array params from query string
  const toArray = (val: unknown): string[] | undefined => {
    if (!val) return undefined;
    if (Array.isArray(val)) return val as string[];
    if (typeof val === "string") return [val];
    return undefined;
  };

  return {
    manufacturer: toArray(params.manufacturer),
    model: toArray(params.model),
    firmware_version: toArray(params.firmware_version),
    configuration_state: toArray(params.configuration_state),
    connectivity: toArray(params.connectivity),
  };
}

export default function GatewayConnectivityReportPage(props: {
  assigned?: boolean;
}): React.ReactElement {
  const { assigned = true } = props;
  const { params: filterParams, ready } =
    useGetParsedSearchString<GatewayFilterParams>(parseGatewayFilterParams);

  if (!ready) {
    return <></>;
  }

  return (
    <OneCardPage
      headerTitle={assigned ? "Gateway Status" : "Unassigned Gateway Status"}
      breadcrumbs={[{ url: "/admin/report/gateway", name: "Gateway Status" }]}
      showSchemeInBreadcrumbs={assigned}
      setPageTitle={schemeObjectPageTitle(
        `Gateway Status${!assigned ? ": Unassigned" : ""}`,
      )}
    >
      <>
        {assigned ? (
          <CompanyNavLink to={{ pathname: "/admin/report/gateway/unassigned" }}>
            Switch to unassigned gateways
          </CompanyNavLink>
        ) : (
          <CompanyNavLink to={{ pathname: "/admin/report/gateway" }}>
            Switch to assigned gateways
          </CompanyNavLink>
        )}{" "}
        <BetaAlert title={"Gateway Filters"} />
        {assigned ? (
          <SelectedScheme>
            {(props) => (
              <GatewayConnectivityReport
                {...props}
                filterParams={filterParams}
              />
            )}
          </SelectedScheme>
        ) : (
          <SelectedScheme>
            {({ company_id }) => (
              <AssetList
                company_id={company_id}
                assetRow={UnassignedGatewayConnectivityReportDataRow}
                headerRow={GatewayConnectivityReportHeaderRow(false)(false)}
                params={{
                  asset_type: [assetTypeEnum.gateway],
                  asset_position_id: "NONE",
                  sort: assetSortEnum.last_contact,
                }}
              />
            )}
          </SelectedScheme>
        )}
      </>
    </OneCardPage>
  );
}
